import { IPathfinderSearchableMap } from "./IPathfinderSearchableMap";

/**
 * A PathfindingGraph object represents a graph of connected nodes that can be traversed by the <strong>Pathfinder</strong>.
 * @author npuschke
 *
 */
export class PathfindingGraph {
    private static TRAVERSE_COST_STANDARD: number = 100;
    private static TRAVERSE_COST_DIAGONAL: number = 133;
    private _nodeX: number[];
    private _nodeY: number[];
    private _nodeNeighbors: any[];
    private _nodeConnectionCosts: any[];
    private _useVariedCosts: boolean;

    /**
     * Creates an empty graph.
     * @param useVariedCosts Whether the connections in the graph have their own specific traverse cost.
     */
    constructor(useVariedCosts: boolean = false) {
        this._nodeX = [];
        this._nodeY = [];
        this._nodeNeighbors = [];
        this._useVariedCosts = useVariedCosts;

        if (this._useVariedCosts) {
            this._nodeConnectionCosts = [];
        }
    }

    /**
     * Gives the number of nodes.
     * @return The number of nodes in the graph.
     *
     */
    public get nodeCount(): number {
        return this._nodeX.length;
    }

    /**
     * Adds a new node to the graph with the given coordinates.
     * @param x The node's horizontal coordinate.
     * @param y The node's vertical coordinate.
     * @return The new nodes index.
     *
     */
    public addNode(x: number, y: number): number {
        this._nodeX.push(x);
        this._nodeY.push(y);
        this._nodeNeighbors.push([]);

        if (this._useVariedCosts) {
            this._nodeConnectionCosts.push([]);
        }
        return this._nodeX.length - 1;
    }

    /**
     * <p>Connects a node with another, making this connection traversable by the pathfinder.</p>
     * <p>Please note that this connection is unilateral: It can only be traversed from <code>note</code> to <code>neighborNode</code>, not vice versa.</p>
     * @param node The node that gets a new connection to another node.
     * @param neighborNode The node that can be reached via this new connection.
     */
    public addNeighbor(node: number, neighborNode: number, connectionCost: number = PathfindingGraph.TRAVERSE_COST_DIAGONAL): void {
        this._nodeNeighbors[node].push(neighborNode);

        if (this._useVariedCosts) {
            this._nodeConnectionCosts[node].push(connectionCost);
        }
    }

    /**
     * Gives the horizontal coordinate of a node.
     * @param node The node's index.
     * @return The node's horizontal coordinate.
     */
    public getNodeX(node: number): number {
        return this._nodeX[node];
    }

    /**
     * Gives the vertical coordinate of a node.
     * @param node The node's index.
     * @return The node's vertical coordinate
     */
    public getNodeY(node: number): number {
        return this._nodeY[node];
    }

    /**
     * Gives all nodes that can be reached from a certain node.
     * <p>To get the traverse cost to the neighbors, use <code>getConnectionCosts</code>.</p>
     * @param node The node's index.
     * @return All the node's neighbors.
     *
     */
    public getNodeNeighbors(node: number): number[] {
        return this._nodeNeighbors[node];
    }

    /**
     * Gives the traverse costs to all neigbor nodes of a certain node.
     * <p>To get the actual neighbors, use <code>getNodeNeighbors</code>.</p>
     * @param node The node's index.
     * @return All the node's traverse costs to neighbor nodes.
     *
     */
    public getConnectionCosts(node: number): number[] {
        if (this._useVariedCosts) {
            return this._nodeConnectionCosts[node];
        }
        else {
            return null;
        }
    }

    /**
     * Retrieves a node at certain coordinates in the graph.
     * @param x The horizontal coordinates of the node.
     * @param y The vertical coordinates of the node.
     * @return The node's index, if there is a node at the coordinates; <code>-1</code> otherwise.
     *
     */
    public getNodeIndexByPosition(x: number, y: number): number {
        for (let i: number = 0; i < this.nodeCount; i++) {
            if (this._nodeX[i] == x && this._nodeY[i] == y) {
                return i;
            }
        }
        return -1;
    }

    /**
     * Determines whether there is a node at certain coordinates in the graph.
     * @param x The horizontal coordinates of the potential node.
     * @param y The vertical coordinates of the potential node.
     * @return <code>true</code>, if there is a node at the coordinates; <code>false</code> otherwise.
     *
     */
    public hasNodeAtPosition(x: number, y: number): boolean {
        for (let i: number = 0; i < this.nodeCount; i++) {
            if (this._nodeX[i] == x && this._nodeY[i] == y) {
                return true;
            }
        }
        return false;
    }

    /**
     * Gives the standard traverse cost for a node to node connection
     * @return The standard traverse cost.
     *
     */
    public get traverseCostStandard(): number {
        return PathfindingGraph.TRAVERSE_COST_STANDARD;
    }

    /**
     * Creates and fills a graph from an object that implements the IPathfinderSearchableMap interface.
     * @param walkabilityMap The object representing a walkability map.
     * @param useDiagonals Whether the graph nodes get connected diagonally if possible.
     * @return The new graph.
     *
     */
    public static generateFromMap(walkabilityMap: IPathfinderSearchableMap, useDiagonals: boolean = false): PathfindingGraph {
        let width: number = walkabilityMap.getWidth();
        let height: number = walkabilityMap.getHeight();
        // create an empty graph (that supports varied connection cost if diagonals are used)
        let graph: PathfindingGraph = new PathfindingGraph(useDiagonals);
        // create a temporary node map
        let nodeMap: any[] = new Array(width);

        // add walkable tiles as nodes to graph (and store references to them in the node map)
        for (let x: number = 0; x < width; x++) {
            nodeMap[x] = new Array(height);

            for (let y: number = 0; y < height; y++) {
                if (walkabilityMap.isWalkable(x, y)) {
                    nodeMap[x][y] = graph.addNode(x, y);
                }
                else {
                    nodeMap[x][y] = -1;
                }
            }
        }
        let nodeIndex: number;

        // add neighbors to nodes
        for (let x = 0; x < width; x++) {
            for (let y = 0; y < height; y++) {
                nodeIndex = nodeMap[x][y];

                if (nodeIndex >= 0) {
                    // left neighbor
                    if (x > 0 && nodeMap[x - 1][y] >= 0) {
                        graph.addNeighbor(nodeIndex, nodeMap[x - 1][y], PathfindingGraph.TRAVERSE_COST_STANDARD);
                    }

                    // right neighbor
                    if (x < width - 1 && nodeMap[x + 1][y] >= 0) {
                        graph.addNeighbor(nodeIndex, nodeMap[x + 1][y], PathfindingGraph.TRAVERSE_COST_STANDARD);
                    }

                    // top neighbor
                    if (y > 0 && nodeMap[x][y - 1] >= 0) {
                        graph.addNeighbor(nodeIndex, nodeMap[x][y - 1], PathfindingGraph.TRAVERSE_COST_STANDARD);
                    }

                    // bottom neighbor
                    if (y < height - 1 && nodeMap[x][y + 1] >= 0) {
                        graph.addNeighbor(nodeIndex, nodeMap[x][y + 1], PathfindingGraph.TRAVERSE_COST_STANDARD);
                    }

                    //diagonal neighbors
                    if (useDiagonals) {
                        //top left neighbor
                        if (x > 0 && y > 0 && nodeMap[x - 1][y - 1] >= 0 && nodeMap[x - 1][y] >= 0 && nodeMap[x][y - 1] >= 0) {
                            graph.addNeighbor(nodeIndex, nodeMap[x - 1][y - 1], PathfindingGraph.TRAVERSE_COST_DIAGONAL);
                        }

                        //top right neighbor
                        if (x < width - 1 && y > 0 && nodeMap[x + 1][y - 1] >= 0 && nodeMap[x + 1][y] >= 0 && nodeMap[x][y - 1] >= 0) {
                            graph.addNeighbor(nodeIndex, nodeMap[x + 1][y - 1], PathfindingGraph.TRAVERSE_COST_DIAGONAL);
                        }

                        //bottom left neighbor
                        if (x > 0 && y < height - 1 && nodeMap[x - 1][y + 1] >= 0 && nodeMap[x - 1][y] >= 0 && nodeMap[x][y + 1] >= 0) {
                            graph.addNeighbor(nodeIndex, nodeMap[x - 1][y + 1], PathfindingGraph.TRAVERSE_COST_DIAGONAL);
                        }

                        //bottom right neighbor
                        if (x < width - 1 && y < height - 1 && nodeMap[x + 1][y + 1] >= 0 && nodeMap[x + 1][y] >= 0 && nodeMap[x][y + 1] >= 0) {
                            graph.addNeighbor(nodeIndex, nodeMap[x + 1][y + 1], PathfindingGraph.TRAVERSE_COST_DIAGONAL);
                        }
                    }
                }
            }
        }
        return graph;
    }
}
